import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveMediaUrl } from '../utils/styleHelpers';

export const DEFAULT_AGE_VERIFICATION_CONFIG = {
    id: 'ageVerification',
    enabled: false,
    minAge: 21,
    mode: 'buttons', // 'buttons' | 'birthdate' | 'birthyear'
    scope: 'all', // 'all' (entire site) | 'shop' (ecommerce only)
    storageType: 'localStorage', // 'localStorage' | 'sessionStorage' | 'always'
    rememberDays: 30,
    exitUrl: 'https://www.google.com',

    // Content & Copy
    badgeIcon: '🔞',
    logoUrl: '',
    title: 'Age Verification Required',
    message: 'You must be at least {minAge} years of age to enter this website. Please confirm your age to proceed.',
    confirmButtonText: 'I am {minAge} or older — Enter',
    denyButtonText: 'I am under {minAge} — Exit',
    disclaimerText: 'By entering this site, you agree to our Terms of Service and confirm you meet the minimum age requirements.',
    underageMessage: 'Access Denied: You must be at least {minAge} years old to view this content.',
    birthdatePrompt: 'Please enter your date of birth to continue:',

    // Full CSS Styling Settings
    overlayBg: 'rgba(15, 23, 42, 0.94)',
    overlayBlur: '8px',
    modalBg: '#ffffff',
    modalTextColor: '#1e293b',
    modalBorder: '1px solid rgba(226, 232, 240, 0.8)',
    modalRadius: '18px',
    modalShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.45)',
    modalMaxWidth: '480px',
    modalPadding: '36px 32px',
    titleColor: '#0f172a',
    titleSize: '1.65rem',
    titleWeight: '800',
    titleTransform: 'none',
    messageColor: '#475569',
    messageSize: '0.98rem',
    confirmBg: '#2563eb',
    confirmTextColor: '#ffffff',
    confirmHoverBg: '#1d4ed8',
    confirmRadius: '10px',
    confirmPadding: '13px 24px',
    confirmWeight: '700',
    denyBg: '#f1f5f9',
    denyTextColor: '#475569',
    denyHoverBg: '#e2e8f0',
    denyBorder: '1px solid #cbd5e1',
    denyRadius: '10px',
    denyPadding: '12px 20px',
    denyWeight: '600',
    customCss: ''
};

const STORAGE_KEY_PREFIX = 'omnivaso_age_verified_';

/**
 * Helper to check whether the current visitor is verified according to module settings.
 */
export const checkIsAgeVerified = (ageModule) => {
    if (!ageModule || !ageModule.enabled) return true;
    const minAge = ageModule.minAge || 21;
    const storageType = ageModule.storageType || 'localStorage';
    if (storageType === 'always') return false;

    const storage = storageType === 'sessionStorage' ? window.sessionStorage : window.localStorage;
    const key = `${STORAGE_KEY_PREFIX}${minAge}`;

    try {
        const raw = storage.getItem(key);
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.verified) return false;

        // Check expiration if localStorage
        if (storageType === 'localStorage' && ageModule.rememberDays) {
            const maxAgeMs = (parseInt(ageModule.rememberDays, 10) || 30) * 86400000;
            if (Date.now() - (parsed.timestamp || 0) > maxAgeMs) {
                storage.removeItem(key);
                return false;
            }
        }
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Helper to mark visitor as verified in storage.
 */
export const setAgeVerified = (ageModule) => {
    const minAge = ageModule?.minAge || 21;
    const storageType = ageModule?.storageType || 'localStorage';
    if (storageType === 'always') return;

    const storage = storageType === 'sessionStorage' ? window.sessionStorage : window.localStorage;
    const key = `${STORAGE_KEY_PREFIX}${minAge}`;
    try {
        storage.setItem(key, JSON.stringify({
            verified: true,
            minAge,
            timestamp: Date.now()
        }));
    } catch (e) {}
};

/**
 * Helper to clear verification (e.g. for testing in dashboard).
 */
export const clearAgeVerification = (minAge = null) => {
    try {
        if (minAge) {
            window.localStorage.removeItem(`${STORAGE_KEY_PREFIX}${minAge}`);
            window.sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${minAge}`);
        } else {
            // Clear all age verification keys
            Object.keys(window.localStorage).forEach(k => {
                if (k.startsWith(STORAGE_KEY_PREFIX)) window.localStorage.removeItem(k);
            });
            Object.keys(window.sessionStorage).forEach(k => {
                if (k.startsWith(STORAGE_KEY_PREFIX)) window.sessionStorage.removeItem(k);
            });
        }
    } catch (e) {}
};

export const AgeVerificationOverlay = ({
    config = {},
    isPreview = false,
    onClosePreview = null
}) => {
    const location = useLocation();
    const pathname = location?.pathname || '';

    // Merge module configuration with defaults
    const ageModule = useMemo(() => {
        const mod = config?.modules?.ageVerification || {};
        return {
            ...DEFAULT_AGE_VERIFICATION_CONFIG,
            ...mod
        };
    }, [config?.modules?.ageVerification]);

    const isEnabled = isPreview || (ageModule.enabled === true || ageModule.enabled === 'true');

    // Verification and state hooks
    const [isVerified, setIsVerified] = useState(() => {
        if (isPreview) return false;
        return checkIsAgeVerified(ageModule);
    });
    const [underageDenied, setUnderageDenied] = useState(false);
    const [birthDate, setBirthDate] = useState('');
    const [birthYear, setBirthYear] = useState('');
    const [inputError, setInputError] = useState('');

    // Re-check when route changes or module configuration changes
    useEffect(() => {
        if (isPreview) {
            setIsVerified(false);
            setUnderageDenied(false);
            return;
        }
        setIsVerified(checkIsAgeVerified(ageModule));
    }, [ageModule, pathname, isPreview]);

    // Admin lockout protection: never gate admin/login routes unless in active preview mode
    const isAdminRoute = pathname.startsWith('/admin') ||
        pathname.startsWith('/login') ||
        pathname.startsWith('/agency-login');

    // Route scope check: 'all' (sitewide) vs 'shop' (ecommerce routes only)
    const isInScope = useMemo(() => {
        if (isPreview) return true;
        if (isAdminRoute) return false;
        if (ageModule.scope === 'shop') {
            return pathname.startsWith('/shop') ||
                pathname.startsWith('/products') ||
                pathname.startsWith('/product') ||
                pathname.startsWith('/cart') ||
                pathname.startsWith('/category');
        }
        return true;
    }, [ageModule.scope, pathname, isAdminRoute, isPreview]);

    // Lock body scrolling when overlay is visible
    useEffect(() => {
        const shouldLock = isEnabled && !isVerified && isInScope;
        if (shouldLock) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isEnabled, isVerified, isInScope]);

    // Handle Confirm action
    const handleConfirm = useCallback(() => {
        if (isPreview) {
            setIsVerified(true);
            if (typeof onClosePreview === 'function') onClosePreview();
            return;
        }
        setAgeVerified(ageModule);
        setIsVerified(true);
    }, [ageModule, isPreview, onClosePreview]);

    // Handle Deny / Underage action
    const handleDeny = useCallback(() => {
        if (isPreview) {
            setUnderageDenied(true);
            return;
        }
        if (ageModule.exitUrl && ageModule.exitUrl.trim()) {
            let targetUrl = ageModule.exitUrl.trim();
            if (!/^https?:\/\//i.test(targetUrl)) {
                targetUrl = 'https://' + targetUrl;
            }
            window.location.href = targetUrl;
        } else {
            setUnderageDenied(true);
        }
    }, [ageModule.exitUrl, isPreview]);

    // Handle Birthdate / Birth Year submission
    const handleDateSubmit = useCallback((e) => {
        if (e) e.preventDefault();
        setInputError('');
        const minAge = parseInt(ageModule.minAge, 10) || 21;

        if (ageModule.mode === 'birthyear') {
            const year = parseInt(birthYear, 10);
            const currentYear = new Date().getFullYear();
            if (!year || isNaN(year) || year < 1900 || year > currentYear) {
                setInputError('Please enter a valid 4-digit birth year.');
                return;
            }
            if (currentYear - year < minAge) {
                setUnderageDenied(true);
                return;
            }
            handleConfirm();
        } else {
            // 'birthdate'
            if (!birthDate) {
                setInputError('Please select or enter your date of birth.');
                return;
            }
            const dob = new Date(birthDate);
            if (isNaN(dob.getTime())) {
                setInputError('Please enter a valid date.');
                return;
            }
            const today = new Date();
            let age = today.getFullYear() - dob.getFullYear();
            const m = today.getMonth() - dob.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
                age--;
            }
            if (age < minAge) {
                setUnderageDenied(true);
                return;
            }
            handleConfirm();
        }
    }, [ageModule.minAge, ageModule.mode, birthDate, birthYear, handleConfirm]);

    // If module is disabled, already verified, or route is out of scope, render nothing
    if (!isEnabled || isVerified || !isInScope) {
        return null;
    }

    const minAge = ageModule.minAge || 21;
    const formatCopy = (str = '') => {
        if (!str) return '';
        // 1. Replace explicit {minAge} dynamic token
        let res = str.replace(/\{minAge\}/g, String(minAge));
        // 2. Auto-heal: If minAge is not 21 and copy contains hardcoded legacy '21', auto-sync it to minAge
        if (minAge !== 21 && /\b21\b/.test(res)) {
            res = res.replace(/\b21\b/g, String(minAge));
        }
        return res;
    };

    // Dynamic styling resolution
    const overlayStyle = {
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        backgroundColor: ageModule.overlayBg || 'rgba(15, 23, 42, 0.94)',
        backdropFilter: ageModule.overlayBlur ? `blur(${ageModule.overlayBlur})` : 'none',
        WebkitBackdropFilter: ageModule.overlayBlur ? `blur(${ageModule.overlayBlur})` : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        boxSizing: 'border-box',
        overflowY: 'auto'
    };

    const cardStyle = {
        position: 'relative',
        width: '100%',
        maxWidth: ageModule.modalMaxWidth || '480px',
        backgroundColor: ageModule.modalBg || '#ffffff',
        color: ageModule.modalTextColor || '#1e293b',
        border: ageModule.modalBorder || '1px solid rgba(226, 232, 240, 0.8)',
        borderRadius: ageModule.modalRadius || '18px',
        boxShadow: ageModule.modalShadow || '0 25px 60px -15px rgba(0, 0, 0, 0.45)',
        padding: ageModule.modalPadding || '36px 32px',
        boxSizing: 'border-box',
        textAlign: 'center',
        animation: 'omnivasoAgeModalPop 0.28s cubic-bezier(0.16, 1, 0.3, 1)'
    };

    const confirmBtnStyle = {
        background: ageModule.confirmBg || '#2563eb',
        color: ageModule.confirmTextColor || '#ffffff',
        border: 'none',
        borderRadius: ageModule.confirmRadius || '10px',
        padding: ageModule.confirmPadding || '13px 24px',
        fontSize: '1rem',
        fontWeight: ageModule.confirmWeight || '700',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        flex: 1,
        minWidth: '180px',
        boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)'
    };

    const denyBtnStyle = {
        background: ageModule.denyBg || '#f1f5f9',
        color: ageModule.denyTextColor || '#475569',
        border: ageModule.denyBorder || '1px solid #cbd5e1',
        borderRadius: ageModule.denyRadius || '10px',
        padding: ageModule.denyPadding || '12px 20px',
        fontSize: '0.95rem',
        fontWeight: ageModule.denyWeight || '600',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        flex: 1,
        minWidth: '160px'
    };

    const resolvedLogoUrl = ageModule.logoUrl ? resolveMediaUrl(ageModule.logoUrl) : null;

    return (
        <div className="omnivaso-age-gatekeeper-overlay" style={overlayStyle} role="dialog" aria-modal="true" aria-labelledby="age-gate-title">
            {/* Scoped CSS animation and custom CSS rules */}
            <style>{`
                @keyframes omnivasoAgeModalPop {
                    0% { transform: scale(0.92); opacity: 0; }
                    100% { transform: scale(1); opacity: 1; }
                }
                .omnivaso-age-confirm-btn:hover {
                    background: ${ageModule.confirmHoverBg || '#1d4ed8'} !important;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 18px rgba(37, 99, 235, 0.35) !important;
                }
                .omnivaso-age-deny-btn:hover {
                    background: ${ageModule.denyHoverBg || '#e2e8f0'} !important;
                    transform: translateY(-1px);
                }
                ${ageModule.customCss || ''}
            `}</style>

            <div className="omnivaso-age-gatekeeper-card" style={cardStyle}>
                {/* Admin Preview Header Tag */}
                {isPreview && (
                    <div style={{
                        position: 'absolute',
                        top: '-12px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#f59e0b',
                        color: '#ffffff',
                        padding: '3px 12px',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                    }}>
                        Interactive Test Preview
                    </div>
                )}

                {/* Underage Denied State */}
                {underageDenied ? (
                    <div className="omnivaso-age-denied-block">
                        <div style={{ fontSize: '3rem', marginBottom: '14px', lineHeight: 1 }}>🚫</div>
                        <h2 style={{
                            margin: '0 0 12px 0',
                            fontSize: '1.45rem',
                            fontWeight: '800',
                            color: '#dc2626'
                        }}>
                            Access Restricted
                        </h2>
                        <p style={{
                            margin: '0 0 24px 0',
                            fontSize: '0.98rem',
                            color: ageModule.messageColor || '#475569',
                            lineHeight: 1.55
                        }}>
                            {formatCopy(ageModule.underageMessage || 'Access Denied: You must be at least {minAge} years old to view this content.')}
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                className="omnivaso-age-deny-btn"
                                style={denyBtnStyle}
                                onClick={() => {
                                    setUnderageDenied(false);
                                    setInputError('');
                                }}
                            >
                                ← Try Again
                            </button>
                            <button
                                type="button"
                                className="omnivaso-age-confirm-btn"
                                style={{
                                    ...confirmBtnStyle,
                                    background: '#dc2626',
                                    boxShadow: '0 4px 14px rgba(220, 38, 38, 0.3)'
                                }}
                                onClick={() => {
                                    const exit = ageModule.exitUrl ? ageModule.exitUrl.trim() : 'https://www.google.com';
                                    window.location.href = /^https?:\/\//i.test(exit) ? exit : 'https://' + exit;
                                }}
                            >
                                Exit Site
                            </button>
                        </div>
                    </div>
                ) : (
                    /* Main Verification Prompt */
                    <div className="omnivaso-age-prompt-block">
                        {/* Logo or Badge Icon */}
                        {resolvedLogoUrl ? (
                            <div style={{ marginBottom: '16px' }}>
                                <img
                                    src={resolvedLogoUrl}
                                    alt="Logo"
                                    style={{
                                        maxHeight: '64px',
                                        maxWidth: '180px',
                                        objectFit: 'contain',
                                        display: 'inline-block'
                                    }}
                                />
                            </div>
                        ) : ageModule.badgeIcon ? (
                            <div style={{ fontSize: '3.2rem', marginBottom: '12px', lineHeight: 1 }}>
                                {ageModule.badgeIcon}
                            </div>
                        ) : null}

                        {/* Title */}
                        <h2
                            id="age-gate-title"
                            style={{
                                margin: '0 0 12px 0',
                                fontSize: ageModule.titleSize || '1.65rem',
                                color: ageModule.titleColor || '#0f172a',
                                fontWeight: ageModule.titleWeight || '800',
                                textTransform: ageModule.titleTransform || 'none',
                                letterSpacing: '-0.02em',
                                lineHeight: 1.25
                            }}
                        >
                            {formatCopy(ageModule.title || 'Age Verification Required')}
                        </h2>

                        {/* Message Description */}
                        <p style={{
                            margin: '0 0 24px 0',
                            fontSize: ageModule.messageSize || '0.98rem',
                            color: ageModule.messageColor || '#475569',
                            lineHeight: 1.55
                        }}>
                            {formatCopy(ageModule.message || 'You must be at least 21 years of age to enter this website. Please confirm your age to proceed.')}
                        </p>

                        {/* Form / Button Controls based on mode */}
                        {ageModule.mode === 'birthdate' || ageModule.mode === 'birthyear' ? (
                            <form onSubmit={handleDateSubmit} style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '0.88rem',
                                    fontWeight: '600',
                                    color: ageModule.modalTextColor || '#334155',
                                    marginBottom: '8px'
                                }}>
                                    {ageModule.birthdatePrompt || (ageModule.mode === 'birthyear' ? 'Enter your birth year (YYYY):' : 'Enter your date of birth:')}
                                </label>
                                {ageModule.mode === 'birthyear' ? (
                                    <input
                                        type="number"
                                        min="1900"
                                        max={new Date().getFullYear()}
                                        placeholder="e.g. 2000"
                                        value={birthYear}
                                        onChange={e => setBirthYear(e.target.value)}
                                        style={{
                                            width: '100%',
                                            maxWidth: '220px',
                                            padding: '11px 14px',
                                            fontSize: '1.1rem',
                                            textAlign: 'center',
                                            borderRadius: '8px',
                                            border: '1px solid #cbd5e1',
                                            margin: '0 auto 14px auto',
                                            display: 'block',
                                            boxSizing: 'border-box'
                                        }}
                                        required
                                    />
                                ) : (
                                    <input
                                        type="date"
                                        value={birthDate}
                                        onChange={e => setBirthDate(e.target.value)}
                                        style={{
                                            width: '100%',
                                            maxWidth: '240px',
                                            padding: '10px 14px',
                                            fontSize: '1rem',
                                            borderRadius: '8px',
                                            border: '1px solid #cbd5e1',
                                            margin: '0 auto 14px auto',
                                            display: 'block',
                                            boxSizing: 'border-box'
                                        }}
                                        required
                                    />
                                )}
                                {inputError && (
                                    <div style={{ color: '#dc2626', fontSize: '0.84rem', marginBottom: '12px', fontWeight: '500' }}>
                                        {inputError}
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <button
                                        type="button"
                                        className="omnivaso-age-deny-btn"
                                        style={denyBtnStyle}
                                        onClick={handleDeny}
                                    >
                                        {formatCopy(ageModule.denyButtonText || 'Exit')}
                                    </button>
                                    <button
                                        type="submit"
                                        className="omnivaso-age-confirm-btn"
                                        style={confirmBtnStyle}
                                    >
                                        Submit & Enter
                                    </button>
                                </div>
                            </form>
                        ) : (
                            /* Classic 1-Click Confirmation Buttons */
                            <div style={{
                                display: 'flex',
                                gap: '12px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                marginBottom: '20px'
                            }}>
                                <button
                                    type="button"
                                    className="omnivaso-age-deny-btn"
                                    style={denyBtnStyle}
                                    onClick={handleDeny}
                                >
                                    {formatCopy(ageModule.denyButtonText || 'I am under 21 — Exit')}
                                </button>
                                <button
                                    type="button"
                                    className="omnivaso-age-confirm-btn"
                                    style={confirmBtnStyle}
                                    onClick={handleConfirm}
                                >
                                    {formatCopy(ageModule.confirmButtonText || 'I am 21 or older — Enter')}
                                </button>
                            </div>
                        )}

                        {/* Legal Disclaimer / Footer Text */}
                        {ageModule.disclaimerText && (
                            <p style={{
                                margin: '14px 0 0 0',
                                fontSize: '0.78rem',
                                color: '#94a3b8',
                                lineHeight: 1.45,
                                borderTop: '1px solid #f1f5f9',
                                paddingTop: '14px'
                            }}>
                                {formatCopy(ageModule.disclaimerText)}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AgeVerificationOverlay;

